import React, { useState, useEffect } from 'react';
import { CheckCircle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Composant de sélection de métiers COPPAM avec codes et coefficients
 * Affiche les métiers par catégorie avec leurs informations
 */
export default function MetierSelector({ 
  selectedMetiers = {}, 
  onChange, 
  showCoefficients = true,
  categories = ['gros_oeuvre', 'second_oeuvre', 'travaux_publics', 'pib']
}) {
  const [metiersData, setMetiersData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});

  useEffect(() => {
    loadMetiers();
  }, []);

  const loadMetiers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/metiers/categories`);
      if (response.ok) {
        const data = await response.json();
        setMetiersData(data.categories);
        
        // Expand all categories by default
        const expanded = {};
        categories.forEach(cat => expanded[cat] = true);
        setExpandedCategories(expanded);
      } else {
        throw new Error('Erreur de chargement');
      }
    } catch {
      setError('Impossible de charger les métiers');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const handleMetierToggle = (category, metier) => {
    const currentCategoryMetiers = selectedMetiers[category] || [];
    let newMetiers;

    if (currentCategoryMetiers.includes(metier)) {
      // Retirer le métier
      newMetiers = currentCategoryMetiers.filter(m => m !== metier);
    } else {
      // Ajouter le métier
      newMetiers = [...currentCategoryMetiers, metier];
    }

    onChange({
      ...selectedMetiers,
      [category]: newMetiers
    });
  };

  const isMetierSelected = (category, metier) => {
    return (selectedMetiers[category] || []).includes(metier);
  };

  const getCategoryColor = (category) => {
    const colors = {
      gros_oeuvre: 'bg-blue-600 border-blue-700 text-white',
      second_oeuvre: 'bg-green-600 border-green-700 text-white',
      travaux_publics: 'bg-purple-600 border-purple-700 text-white',
      pib: 'bg-orange-600 border-orange-700 text-white'
    };
    return colors[category] || 'bg-gray-600 border-gray-700 text-white';
  };

  const getSelectedCount = () => {
    return Object.values(selectedMetiers).reduce((total, metiers) => total + metiers.length, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 md:p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Chargement des métiers...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-300 text-red-900 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Compteur de sélection */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-blue-900">Sélection des Activités</h3>
            <p className="text-sm text-blue-700">
              {getSelectedCount()} activité(s) sélectionnée(s)
            </p>
          </div>
          {showCoefficients && (
            <div className="text-xs text-blue-600 flex items-center">
              <Info className="w-4 h-4 mr-1" />
              Coefficients de risque affichés
            </div>
          )}
        </div>
      </div>

      {/* Liste des catégories */}
      {categories.map(category => {
        if (!metiersData[category]) return null;

        const categoryData = metiersData[category];
        const isExpanded = expandedCategories[category];

        return (
          <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Header de catégorie */}
            <button
              onClick={() => toggleCategory(category)}
              className={`w-full px-4 py-3 flex items-center justify-between ${getCategoryColor(category)} hover:opacity-80 transition-opacity`}
            >
              <div className="flex items-center">
                <span className="font-semibold text-lg">{categoryData.label}</span>
                <span className="ml-3 text-sm opacity-75">
                  ({Object.keys(categoryData.metiers).length} métiers)
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">
                  {(selectedMetiers[category] || []).length} sélectionné(s)
                </span>
                <span className="text-xl">{isExpanded ? '▼' : '▶'}</span>
              </div>
            </button>

            {/* Liste des métiers */}
            {isExpanded && (
              <div className="bg-white divide-y divide-gray-100">
                {Object.entries(categoryData.metiers).map(([metierName, metierInfo]) => {
                  const isSelected = isMetierSelected(category, metierName);

                  return (
                    <div
                      key={metierName}
                      onClick={() => handleMetierToggle(category, metierName)}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                        isSelected ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 flex-1">
                          {/* Checkbox */}
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              isSelected
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>

                          {/* Nom du métier */}
                          <span className={`font-medium ${isSelected ? 'text-blue-900' : 'text-gray-800'}`}>
                            {metierName}
                          </span>
                        </div>

                        {/* Code et coefficient */}
                        <div className="flex items-center space-x-4 text-sm">
                          <span className="px-2 py-1 bg-gray-600 text-white rounded font-mono text-xs">
                            {metierInfo.code}
                          </span>
                          {showCoefficients && (
                            <span className={`px-2 py-1 rounded font-medium ${
                              isSelected ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                              Coef: {metierInfo.coefficient}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* Aide */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-600">
        <p className="flex items-start">
          <Info className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            Les coefficients de risque sont utilisés pour calculer votre contribution.
            Plus le coefficient est élevé, plus l'activité est considérée comme à risque.
          </span>
        </p>
      </div>
    </div>
  );
}
